import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import AppButton from '../AppButton/AppButton';

/**
 * DrawingCanvas — generalized freehand-drawing atom built on `react-signature-canvas`.
 *
 * Lifted from `SignaturePad` (see `../SignaturePad/SignaturePad.tsx`, now a thin
 * wrapper around this component) so the same base64-PNG-out canvas can be reused
 * for the exam paper builder's Math/Drawing question cards, not just e-signatures.
 * All copy that was hardcoded in `SignaturePad` is exposed here as overridable
 * props (`capturedLabel`, `notCapturedLabel`, `helperTextDisabled`,
 * `helperTextEnabled`) — `SignaturePad` passes the exact original strings as
 * defaults so its two existing call sites see zero behavior change.
 */
export interface DrawingCanvasProps {
  // Core props
  value?: string; // base64 drawing data URL
  onChange: (drawingData: string) => void; // callback with base64 output
  onError?: (error: string) => void; // error callback

  // State props
  disabled?: boolean;
  error?: string | null;
  showErrorMessage?: boolean; // show error message below canvas

  // Styling props
  width?: string; // Tailwind class (default: 'w-full')
  height?: string; // Tailwind class for height (default: 'h-40')
  penColor?: string; // pen color (default: brand purple)
  backgroundColor?: string; // canvas background (default: white)
  borderClassName?: string; // custom border class
  containerClassName?: string; // custom container class
  canvasClassName?: string; // custom canvas class
  labelClassName?: string; // custom label class

  // Canvas config props
  label?: string; // optional label above canvas
  showClearButton?: boolean; // show clear button (default: true)
  showStatus?: boolean; // show captured/not-captured status (default: true)

  // Overridable copy (defaults mirror the original SignaturePad strings)
  capturedLabel?: string; // status text once something is drawn
  notCapturedLabel?: string; // status text while canvas is empty
  helperTextDisabled?: string; // helper text shown under the canvas while disabled
  helperTextEnabled?: string; // helper text shown under the canvas while enabled
  clearButtonLabel?: string;

  // Pen stroke config (optional)
  penWidthMin?: number; // default: 0.5
  penWidthMax?: number; // default: 2.5
  velocityFilterWeight?: number; // default: 0.7
  minDistance?: number; // default: 5
  throttle?: number; // default: 16

  // Accessibility
  ariaLabel?: string;
  ariaDescribedBy?: string;
}

const DrawingCanvas = React.forwardRef<SignatureCanvas, DrawingCanvasProps>(
  (
    {
      value = '',
      onChange,
      onError,
      disabled = false,
      error = null,
      showErrorMessage = true,
      width = 'w-full',
      height = 'h-40',
      penColor = '#6c4e9a', // Classveew brand purple
      backgroundColor = 'rgba(255, 255, 255, 1)',
      borderClassName = '',
      containerClassName = '',
      canvasClassName = '',
      labelClassName = '',
      label,
      showClearButton = true,
      showStatus = true,
      // The four signature-flavored copy defaults stay as-is: `SignaturePad`
      // (the only caller) passes translated overrides for all of them, so these
      // are just the un-overridden fallback for a future generic caller.
      capturedLabel = 'Signature captured',
      notCapturedLabel = 'Not signed',
      helperTextDisabled = 'Sign after you read the agreement',
      helperTextEnabled = 'Draw your signature above with mouse or finger',
      clearButtonLabel,
      penWidthMin = 0.5,
      penWidthMax = 2.5,
      velocityFilterWeight = 0.7,
      minDistance = 5,
      throttle = 16,
      ariaLabel = 'Draw your signature',
      ariaDescribedBy,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const resolvedClearLabel = clearButtonLabel ?? t('common.button_clear', 'Clear');
    const canvasRef = useRef<SignatureCanvas>(null);
    const [isSigned, setIsSigned] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);

    // Update ref to use our canvas ref
    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(canvasRef.current);
        } else {
          ref.current = canvasRef.current;
        }
      }
    }, [ref]);

    // Load existing drawing from value prop
    useEffect(() => {
      if (value && canvasRef.current && !isDrawing) {
        try {
          canvasRef.current.fromDataURL(value);
          setIsSigned(true);
        } catch (err) {
          console.warn('Failed to load drawing from data URL:', err);
        }
      }
    }, [value, isDrawing]);

    // Handle disabled state
    useEffect(() => {
      if (canvasRef.current) {
        if (disabled) {
          canvasRef.current.off(); // Disable drawing
        } else {
          canvasRef.current.on(); // Enable drawing
        }
      }
    }, [disabled]);

    // Handle drawing start
    const handleDrawBegin = useCallback(() => {
      setIsDrawing(true);
    }, []);

    // Handle drawing end - export to base64
    const handleDrawEnd = useCallback(() => {
      setIsDrawing(false);

      if (canvasRef.current) {
        // Check if drawing is empty
        if (canvasRef.current.isEmpty()) {
          setIsSigned(false);
          onChange('');
          return;
        }

        // Export to base64
        try {
          const drawingData = canvasRef.current.toDataURL('image/png');
          setIsSigned(true);
          onChange(drawingData);

          // Clear error on successful drawing
          if (onError) {
            onError('');
          }
        } catch (err) {
          const errorMsg = t('drawing_board.msg_failed_capture', 'Failed to capture drawing');
          console.error(errorMsg, err);
          if (onError) {
            onError(errorMsg);
          }
        }
      }
    }, [onChange, onError, t]);

    // Handle clear button
    const handleClear = useCallback(() => {
      if (canvasRef.current) {
        canvasRef.current.clear();
        setIsSigned(false);
        onChange('');
      }
    }, [onChange]);

    // Determine border color based on state
    const borderClass = borderClassName
      ? borderClassName
      : error
      ? 'border-red-500 border-2'
      : isSigned
      ? 'border-green-500 border-2'
      : 'border border-gray-300';

    // Determine focus ring class
    const focusRingClass = disabled ? '' : 'focus-within:ring-1 focus-within:ring-[#6c4e9a]';

    // Determine cursor based on disabled state
    const cursorClass = disabled ? 'cursor-not-allowed' : 'cursor-crosshair';

    return (
      <div className={`flex flex-col gap-2 ${containerClassName}`}>
        {/* Label */}
        {label && (
          <label className={`text-sm font-semibold text-gray-700 font-poppins ${labelClassName}`}>
            {label}
          </label>
        )}

        {/* Canvas Container */}
        <div
          className={`${width} rounded-md overflow-hidden ${borderClass} ${focusRingClass} transition-all duration-200`}
        >
          <SignatureCanvas
            ref={canvasRef}
            penColor={penColor}
            canvasProps={{
              className: `${height} ${width} ${cursorClass} bg-white ${canvasClassName}`,
              'aria-label': ariaLabel,
              'aria-describedby': ariaDescribedBy,
              'aria-invalid': !!error,
            }}
            backgroundColor={backgroundColor}
            onEnd={handleDrawEnd}
            onBegin={handleDrawBegin}
            velocityFilterWeight={velocityFilterWeight}
            minWidth={penWidthMin}
            maxWidth={penWidthMax}
            minDistance={minDistance}
            throttle={throttle}
          />
        </div>

        {/* Status & Controls Row */}
        <div className="flex items-center justify-between gap-3">
          {/* Status indicator - grows to take available space */}
          {showStatus && (
            <div className="flex-1 text-xs font-poppins">
              {isSigned ? (
                <span className="flex items-center gap-1 text-green-600">
                  <span className="inline-block w-2 h-2 bg-green-600 rounded-full"></span>
                  {capturedLabel}
                </span>
              ) : (
                <span className="text-gray-500">{notCapturedLabel}</span>
              )}
            </div>
          )}

          {/* Clear Button - fixed width (w-1/4) and aligned on same row */}
          {showClearButton && isSigned && (
            <div className="w-1/4 flex-shrink-0">
              <AppButton
                label={resolvedClearLabel}
                text
                onClick={handleClear}
                disabled={disabled}
                className="w-full text-xs px-3 py-1"
                // A full phrase, not `${label} drawing` — glueing a translated
                // word onto an English noun produces nonsense in other languages.
                aria-label={t('drawing_board.aria_clear', 'Clear drawing')}
              />
            </div>
          )}
        </div>

        {/* Error Message */}
        {showErrorMessage && error && (
          <p className="text-xs text-red-600 mt-1 font-poppins">{error}</p>
        )}

        {/* Helper Text */}
        {!isSigned && (
          <p className="text-xs text-gray-500 font-poppins italic">
            {disabled ? helperTextDisabled : helperTextEnabled}
          </p>
        )}
      </div>
    );
  },
);

DrawingCanvas.displayName = 'DrawingCanvas';

export default DrawingCanvas;
